import styles from "./cards-list.module.css";
import ListingCard from "../listing-cards/listing-card";
import { usePageStore } from "../../usePageStore";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { ListingCardData, ListingTypeEnum } from "@/types/types";
import { Pagination } from "@/components/pagination/pagination";
import ListingCardLoading from "../listing-cards/listing-card-loading";
import AdSlot from "@/components/ads/ad-slot-list";
import ListingCardPremium from "@/components-v2/listing-cards/listing-card-premium";

const WEIGHT_LIMIT = 6;
const CARD_WEIGHT = {
  PREMIUM: 2,
  NORMAL: 1,
};

const AD_SLOTS = ["9107600108", "8497866647", "7930373511"];

export default function CardsListClient({ locale }: { locale: string }) {
  const listingsIds = usePageStore(
    (state) => state.ListingCardsState.listingsIds,
  );
  const totalCount = usePageStore(
    (state) => state.ListingCardsState.total_count,
  );
  const page_number = usePageStore(
    (state) => state.ListingCardsState.page_number,
  );
  const isLoaded = usePageStore((state) => state.isLoaded);
  // const isLoaded = usePageStore((state) => state.isLoaded);
  const fetchPageNumber = usePageStore((state) => state.fetchPageNumber);
  const cachedListings = useListingsCacheStore((state) => state.listings);
  const setCache = useListingsCacheStore((state) => state.setMultipleListings);
  const listings: ListingCardData[] = useMemo(() => {
    return listingsIds.map((id) => cachedListings[id]).filter(Boolean);
  }, [listingsIds, cachedListings]);

  const adAfterIndex = useMemo(() => {
    let weight = 0;
    let adCount = 0;

    return listings.map((card) => {
      const isPremium =
        card.listing_type === ListingTypeEnum.PRIME ||
        card.listing_type === ListingTypeEnum.SPOTLIGHT;

      weight += isPremium ? CARD_WEIGHT.PREMIUM : CARD_WEIGHT.NORMAL;

      if (weight >= WEIGHT_LIMIT) {
        weight = 0;
        return AD_SLOTS[adCount++ % AD_SLOTS.length];
      }

      return null;
    });
  }, [listings]);

  return (
    <>
      <ul className={styles.cardsList}>
        {!isLoaded &&
          [...Array(6)].map((_, i) => {
            return (
              <li className={styles.listItem} key={i}>
                <ListingCardLoading activePlan="prime" />
              </li>
            );
          })}
        {listings.map((cardData, index) => {
          const adSlot = adAfterIndex[index];
          return (
            <React.Fragment key={cardData.listing_id}>
              <li className={`${styles.listItem} ${styles.itemWrapper}`}>
                {cardData.listing_type === ListingTypeEnum.PRIME ||
                cardData.listing_type === ListingTypeEnum.SPOTLIGHT ? (
                  <ListingCardPremium data={cardData} />
                ) : (
                  <ListingCard
                    data={cardData}
                    isResizable={true}
                    hasActions={false}
                    hasRoundButtons={true}
                  />
                )}
              </li>
              {adSlot && (
                <li className={styles.listItem} key={`ad-${index}`}>
                  <div className={styles.adWrapper}>
                    <AdSlot slot={adSlot} />
                  </div>
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ul>
      <div className={styles.paginationWrapper}>
        <Pagination
          total={totalCount}
          pageSize={32}
          currentPage={page_number}
          onPageChange={async (number) => {
            if (number === page_number) return;
            await fetchPageNumber(number, locale, setCache);
          }}
        />
      </div>
    </>
  );
}

export function CardsListLoading() {
  return (
    <>
      <ul className={styles.cardsList}>
        {[...Array(6)].map((_, i) => {
          return (
            <li className={styles.listItem} key={i}>
              <ListingCardLoading activePlan="prime" />
            </li>
          );
        })}
      </ul>
      <div className={styles.paginationWrapper}></div>
    </>
  );
}
